import { DutyHourTypeDynFlag, DutyHourTypeFlag } from 'generated';

import {
  DutyLineFlag as DutyLineFlagEnum,
  EmployeeCostCenterType as EmployeeCostCenterTypeEnum,
  EmployeeType as EmployeeTypeEnum,
  PlanUnitType as PlanUnitTypeEnum,
  ScenarioAdditionalCapacityType as ScenarioAdditionalCapacityTypeEnum,
  SelfMatchAction as SelfMatchActionEnum,
  StatusSvc as StatusSvcEnum,
  Units as UnitsEnum
} from './enums';
import { Dayjs } from 'dayjs';

export type DutyLineFlag = keyof typeof DutyLineFlagEnum;
export type StatusSvc = keyof typeof StatusSvcEnum;
export type EmployeeType = keyof typeof EmployeeTypeEnum;
export type SelfMatchAction = keyof typeof SelfMatchActionEnum;
export type EmployeeCostCenterType = keyof typeof EmployeeCostCenterTypeEnum;

export type Validity = {
  from: string;
  to: string;
};

export type Grant = {
  accountId: number;
  grantId: number;
  disable: boolean;
  name: string;
  type: 'LOGIC' | 'ORG';
  collapse: boolean;
  valid: Validity;
};

type Unit = {
  id: number;
  name: string;
  uniqId: string | number;
};

type PlanUnitType =
  | PlanUnitTypeEnum.COST_CENTER
  | PlanUnitTypeEnum.AGENCY
  | PlanUnitTypeEnum.REGIONAL_POOL
  | PlanUnitTypeEnum.FLEX
  | null;

export type PlanUnit = Unit & {
  isKids?: boolean;
  validity: {
    start: string;
    end: string;
  };
  dynId: number;
  code: string;
  nameShort: string;
  brId: number;
  brDynId?: number;
  valid?: {
    from: string;
    to: string;
  };
  type?: PlanUnitType;
  category?: string;
  entityId?: string;
};

export type PlanUnitDetails = {
  brId: number;
  code: string;
  name: string;
  nameShort: string;
  brTypeSvc: string;
  brType: string;

  validFrom: string;
  validTo: string;

  budgetKeeper: string;
  manager: string;
  comEmail: string;
  comEmailCc: string;
  priviligedEmailAddress: string;
  comPhone: string;
  comSms: string;
  comTelefax: string;
  issues: string;

  adrResStreet: string;
  adrResPostalCode: string;
  adrResCity: string;
  adrResCountryCode: string;
  brCountry: string;

  requestPool: string;
  employeePool: string;
  remarks: string;
  agenciesRemarks: string;

  isKids: boolean;
  wcaDescription: string;
};

export type PlanGroup = Unit & {
  costCenters: PlanUnit[];
};

export type PlanUnitMix = PlanUnit | PlanGroup;

export type Interval = {
  startDate: Date;
  endDate: Date;
};

export type Preference = {
  accountId: number;
  accountPrefId: number;
  value: string;
  key: string;
};

export type Qualification = {
  code: string;
  id: string;
  name: string;
  canBeEdited: boolean;
};

export type Holiday = {
  date: string;
  description: string;
  holidayId: number;
  holidayCompLeaveHours?: number;
};

export type WorkAgreement = {
  benefitId: number;
  brId: number;
  brName: string;
  brNameShort: string;
  contractCode: string;
  contractKind: string;
  empId: number;
  employmentEnd: string;
  employmentStart: string;
  flags: string[];
  fte: number;
  functionDescription: string;
  functionId: number;
  impHeaderId: number;
  impSeq: number;
  nr: number;
  pisRegNr: string;
  validFrom: string;
  validTo: string;
  waId: number;
  weekHoursMin?: number;
  displayValue?: string;
};

export type Benefit = {
  costCenterCode: `${number}`;
  costCenterId: number;
  costCenterName: string;
  costCenterNameShort: string;
  empBenefitId: number;
  functionCode: `${number}`;
  functionColor: null | string;
  functionId: number;
  functionName: string;
  hasInvalidFlag: boolean;
  isPartial: boolean;
  parentFunctionId: null | number;
};

export type BenefitDetails = {
  benefitId: number;
  brDynId: number;
  brId: number;
  brName: string;
  brNameShort: string;
  claCode: string;
  claId: number;
  employerName: string;
  flags: string[];
  functionCode: string;
  functionDescription: string;
  functionId: number;
  isValidToday: boolean;
  periodSalaryExtra: number;
  periodSalaryNominal: number;
  poolDescription: string;
  poolId: number;
  validFrom: string;
  validTo: string;
  weekHoursMin?: number;
};

export type WorkAgreementDetails = {
  benefits: BenefitDetails[];
  empId: number;
  employmentEnd: string;
  employmentStart: string;
  nr: number;
  waId: number;
};

export type PlannedDutyHourType = {
  code: string;
  colorCode: string;

  dcc_id: number;
  description: string;
  flags: DutyHourTypeFlag[];
  id: 3;
  isDefault: true;
  isRestricted: false;
};

export type PlannedDutyPeriod = {
  hours: number;
  overlapFlag: number;
  parentId: number;
  startTime: string;
  endTime: string;
  start?: string;
  end?: string;
  dutyHourType: PlannedDutyHourType;
};

export type PlannedDuty = {
  benefitId: number;
  costCenterCode: string;
  costCenterId: number;
  costCenterName: string;
  costCenterNameShort: string;
  date: string;
  dutyLineId: number;
  employeeId: number;
  externalAbsenceMismatch: boolean;
  flags: DutyLineFlag[];
  functionId: number;
  noteCount: number;
  notes: string[];
  periods: PlannedDutyPeriod[];
  publishDate: string;
  statusSvc: StatusSvc;
  workAgreementId: number;
  workAgreementNr: number;
  dutyId?: number;
  dutyCode?: string;
  displayDutyCode?: string;
  displayDutyColor?: string;
  requestId?: number;
  description?: string;
};

export type Pool = {
  name: string;
  id: number;
};

export type EmploymentAgency = {
  code: string;
  dynId: number;
  id: number;
  name: string;
  nameShort: string;
  pool: Pool;
  remarks: string;
  validity: Validity;
  sortOrderAgency?: string;
  type: string;
  privilegedEmailAddress: string;
};

export type RosterFunction = {
  code: string;
  costKind: string;
  description: string;
  eFlags: string[];
  id: number;
  lifetime: {
    start: string;
    end: string;
  };
};

export type PreferredFunction = {
  ccId: number;
  functions: { id: number }[];
  name: string;
  nameShort: string;
};

export type ScenarioCostCenter = {
  id: number;
  name: string;
  nameShort: string;
};

export type Scenario = {
  year: number;
  name: string;
  scenarioId: number;
  costCenters: ScenarioCostCenter[];
  label: string;
  lowerCaseName: string;
  id: number;
  note: string;
  isDefault?: boolean;
  readOnly: boolean;
};

export type EmployeeFunction = {
  code: string;
  colorCode?: string;
  description: string;
  id: number;
  parentFunctionId?: number;
  capPlanExclude?: boolean;
};

export type Employee = {
  id: number;
  number: number;
  name: string;
};

export type Employment = {
  id: number;
  number: number;
};

export type OrganizationalUnit = {
  id: number;
  name: string;
};

export type SharedOrganizationalUnit = {
  organizationalUnit: OrganizationalUnit;
  percentage: number; // 0-1
  hours?: number | null;
  isBlocked: boolean;
  id: string;
};

export type SharedOrganizationalUnitExt = Omit<
  SharedOrganizationalUnit,
  'organizationalUnit'
> & {
  organizationalUnit?: OrganizationalUnit;
  order?: number;
  isNew?: boolean;
};

export type OrganizationalUnitDistribution = {
  id: string;
  employmentId: number;
  startDate: string; // 2022-03-16
  endDate?: string; // 2022-03-16
  employmentHours?: number;
  sharedOrganizationalUnits: SharedOrganizationalUnit[];
};

export type PeriodHour = {
  number: number;
  quarter: number;
  year: number;
  hours: number;
};

export type EmploymentType = {
  name: string;
  code: string;
  description: string;
};

export type EmployeeFormation = {
  sourceOrganizationalUnit: OrganizationalUnit;
  deployedOrganizationalUnit: OrganizationalUnit;
  function: EmployeeFunction;
  employee: Employee;
  employment: Employment;
  employmentType: EmploymentType;
  deployedPercentage: number;
  periodHours: PeriodHour[];
  totalYearHours: number;
  formationPercentage: number;
  excludedFromScenario: boolean;
  uuid: string;
};

export type AbsenceTypeFormation = {
  absenceType: string;
  periodHours: PeriodHour[];
  totalYearHours: number;
  formationPercentage: number;
  uuid: string;
};

export type AdditionalFormationCategory = {
  sourceOrganizationalUnit: OrganizationalUnit;
  deployedOrganizationalUnit: OrganizationalUnit;
  function: EmployeeFunction;
  categoryName: string;
  deployedPercentage: number;
  periodHours: PeriodHour[];
  totalYearHours: number;
  formationPercentage: number;
  excludedFromScenario: boolean;
  uuid: string;
};

export type Formation = {
  periodHours: PeriodHour[];
  totalYearHours: number;
  formationPercentage: number;
  employeeFormations?: EmployeeFormation[];
};

export type AbsenceFormation = {
  periodHours: PeriodHour[];
  totalYearHours: number;
  formationPercentage: number;
  absenceTypeFormations: AbsenceTypeFormation[];
};

export type AdditionalFormation = {
  periodHours: PeriodHour[];
  totalYearHours: number;
  formationPercentage: number;
  additionalFormations?: AdditionalFormationCategory[];
};

export type CLA = {
  claId: number;
  code: string;
};

export type WorkDemandPeriod = {
  number: number;
  hours: number;
  revenue: number;
};

export interface WorkDemandRow {
  id: number;
  costCenterId: number;
  description: string;
  directClientHours: number;
  function?: EmployeeFunction;
  indirectClientHours: number;
  hoursPerYear: number;
  organisationHours: number;
  quantity: number;
  unit: string;
  timeframe: {
    from: string;
    to: string;
  };
}

export type WorkDemandRowWithoutId = Omit<WorkDemandRow, 'id'>;

export type WorkDemand = WorkDemandRow[];

export type CalculatedWorkDemandRow = {
  description: string;
  periods: WorkDemandPeriod[];
};

export type CalculatedWorkDemand = {
  scenarioId: number;
  scenarioYear: string;
  rows: CalculatedWorkDemandRow[];
};

type CostCenter = {
  id: number;
  name: string;
  nameShort: string;
};

export type Baseplan = {
  archived: boolean;
  baseplanId: number;
  costCenters: CostCenter[];
  locked: boolean;
  name: string;
  validFrom: string;
  validTo: string;
  weekCount: number;
};

export type PlannedHour = {
  baseplanId: number;
  scenarioId: number;
  startDate: string;
  endDate: string;
  organizationalUnit: string;
};

export type NonPlannedHour = {
  costCenter: CostCenter;
  description: string;
  dhtHoursCategory: string;
  dhtId: number;
  dhtName: string;
  function: {
    name: string;
    id: number;
    functionFlags: never[];
  };
  hourlyCost: number | 'NaN';
  hoursPerUnit: number;
  hoursYearCostTotal: number;
  hoursYearTotal: number;
  id: number;
  quantity: number;
  scenarioId: number;
  timeframe: {
    from: string;
    to: string;
  };
  unit: string;
};

export type AdditionalCapacity = {
  id: number;
  scenarioId: number;
  type: ScenarioAdditionalCapacityTypeEnum;
  startDate: string;
  endDate: string;
  description: string;
  unit: UnitsEnum;
  hours: number;
  quantity: number;
  function: EmployeeFunction;
  organizationalUnit: PlanUnitMix | null;
  sourceOrganizationalUnit: OrganizationalUnit;
  destinationOrganizationalUnit: OrganizationalUnit;
  status: string;
  createdOn?: string | null;
  rejectedOn?: string | null;
  approvedOn?: string | null;
  reason?: string | null;
} & (
  | {
      status: 'REJECTED';
      rejectedOn: string;
      reason: string;
    }
  | {
      status: 'APPROVED';
      approvedOn: string;
    }
  | {
      status: 'REQUESTED';
      createdOn: string;
    }
);

export type AdditionalCapacityWithoutId = Omit<
  AdditionalCapacity,
  | 'id'
  | 'status'
  | 'createdOn'
  | 'rejectedOn'
  | 'approvedOn'
  | 'sourceOrganizationalUnit'
  | 'destinationOrganizationalUnit'
>;

type AdditionalCapacityHours = {
  periodHours: PeriodHour[];
  totalYearHours: number;
};

export type AgreedAdditionalCapacityHours = {
  periodHours: PeriodHour[];
  totalYearHours: number;
  requestedAdditionalCapacity: AdditionalCapacityHours;
  approvedAdditionalCapacity: AdditionalCapacityHours;
};

export type ActualAdditionalCapacityHours = {
  periodHours: PeriodHour[];
  totalYearHours: number;
  openAdditionalCapacity: AdditionalCapacityHours;
  filledAdditionalCapacity: AdditionalCapacityHours;
};

export type QuantityHour = {
  periodHours: PeriodHour[];
  totalYearHours: number;
  description: string;
  uuid: string;
};

export type FunctionCapacityBalance = {
  periodHourBalances: PeriodHour[];
  totalYearHoursBalance: number;
  function?: EmployeeFunction;
  quantityHours: QuantityHour[];
  uuid: string;
};

export type BaseQueueItem = {
  id: number;
  status: string;
  tenantId: string;
  inserted: string;
  started: string;
  ended: string;
};

export type ImportQueueItem = BaseQueueItem & {
  type: string;
  externalId: number;
  employer: string;
  importedObject: string;
};

export type DataQueueItem = BaseQueueItem & {
  requestType: string;
  requestClass: string;
  requestData: string;
};

export type SchedulingQueueItem = BaseQueueItem & {
  requestType: string;
  requestClass: string;
  requestData: string;
};

export type ReportQueueItem = BaseQueueItem & {
  reportFileId: number;
  accountId: number;
  locale: string;
};

export type BaseQueueItems = BaseQueueItem[];
export type ImportQueue = ImportQueueItem[];
export type DataQueue = DataQueueItem[];
export type SchedulingQueue = SchedulingQueueItem[];
export type ReportQueue = ReportQueueItem[];

export type GetBaseQueueOptions = {
  params: {
    tenantId?: number;
    start?: string;
    end?: string;
  };
  signal?: AbortSignal;
};

export type Absence = {
  startTime: string;
  endTime: string;
  absPercentage: number;
  absenceId: number;
  absenceReasonCode: string;
  dutyTypeColor: string;
  dutyTypeFlags?: string[];
  dutyTypeId: number;
  employeeId?: number;
  flags?: unknown[];
  hoursWeek: number;
  waId: number;
};

export interface Address {
  streetName: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  countryCode: string;
}

export type DeployedVersion = {
  id: number;
  version: string;
  deployed: string;
};
export type VersionHistory = DeployedVersion[];

export interface DutyHourTypeDto {
  code: string;
  colorCode: string;
  dcc: Dcc;
  dccSvc: number;
  description: string;
  dutyTypeDynId: number;
  dutyTypeId: number;
  flags: DutyHourTypeDynFlag[];
  hoursPercComp: number;
  hoursPercSeparateBonusPerc: number;
  hoursPercSeparateBuhPerc: number;
  hoursPercSeparateJusPerc: number;
  hoursPercSeparatePerc: number;
  id: number;
  salPerc: number;
  valid: Valid<string>;
  activityCode?: string;
  activityCodeAlt?: string;
  amountShiftNominal?: number;
  dayCategory?: DayCategory;
  dutyTypeFlags?: DutyHourTypeFlag[];
  hoursCategorySvc?: string;
  reqDisconnectReasonAvId?: number;
  salSupplPerDutyNominal?: number;
  salaryNominalSupplement?: number;
}

export interface DayCategory {
  code: string;
  description: string;
  name: string;
}

export interface Valid<T = Date> {
  from: T;
  to: T;
}

export interface Dcc {
  dccCode: string;
  dccDescription: string;
  flags: DccFlag[];
}

export interface DutyCalculationCodeDto {
  dccId: number;
  code: string;
  flags: DccFlag[];
  description: string;
  buhTypeSvc?: string;
  compPerc: number;
  hoursPerc: number;
  finFactor: number;
  overlapFlag: number;
}

export enum DccFlag {
  'ALLOWED_WITH_DUTY_HOURS' = 'ALLOWED_WITH_DUTY_HOURS',
  'ALLOWED_WITH_NOMINAL' = 'ALLOWED_WITH_NOMINAL',
  'ALLOW_SWAP' = 'ALLOW_SWAP',
  'BONUS' = 'BONUS',
  'BUH' = 'BUH',
  'COMP' = 'COMP',
  'COMP_2' = 'COMP_2',
  'NPH' = 'NPH',
  'REQUEST_VALID' = 'REQUEST_VALID'
}

export interface HourCategory {
  code: string;
  groupId: number;
  name: string;
  id: number;
}

export interface DayCategory {
  code: string;
  groupId: number;
  name: string;
  description: string;
  id: number;
}

export interface DisconnectReason {
  code: string;
  auxValueId: number;
  groupId: number;
  flags: string[];
  name: string;
  id: number;
}

export interface AdminSettings {
  settings: Setting[];
  groupName: string;
  groupId: number;
  active: boolean;
  valid: boolean;
}

export interface Setting {
  groupId: number;
  value: string;
  key: string;
  settingId: number;
}

export type AdminSettingsResponse = AdminSettings[];

export interface AdministrationEmployees {
  accountNr?: string;
  addressingCompound: string;
  adrMailCity?: string;
  adrMailCountryAvId?: number;
  adrMailPostalcode?: string;
  adrMailStreet?: string;
  adrResCity?: string;
  adrResCountryAvId?: number;
  adrResPostalcode?: string;
  adrResStreet?: string;
  availabilitySendBySvc?: string;
  bigRegistration?: string;
  birthDate: string;
  birthPlace?: string;
  comEmail?: string;
  comPhone?: string;
  comSms?: string;
  empId: number;
  flags: string[];
  formation?: number;
  genderSvc?: string;
  maritalStatusAvId?: number;
  nameBirthNames?: string;
  nameCompound: string;
  nameCompoundSurname: string;
  nameFirst: string;
  nameInfix: string;
  nameInformal: string;
  nameInitials: string;
  nameSalutation?: string;
  nameSurname: string;
  nameSurnameCompound: string;
  nameTitlePrefix?: string;
  nameTitleSuffix?: string;
  nationalityAvId?: number;
  nr: number;
  partnerNameInfix: string;
  partnerNameInitials?: string;
  partnerSurname: string;
  socialSecurityNr?: string;
  surnameCompositeSvc: string;
  uniformSize?: string;
  valid: Valid;
  validToReal: string;
  workRelationSvc: string;
}

export interface Account {
  accountId: number;
  nameLast?: string;
  nameFirst?: string;
  accountName: string;
  comEmail?: string;
  empId?: number;
  valid: Valid<null | Dayjs>;
}

export interface AccountDetails extends Account {
  flags: string[];
  employee?: {
    valid: Valid;
    name: string;
  };
  validFromLocked: boolean;
  groups: Group[];
  entityId: string;
  loginFailures: number;
  accountId: number;
  nameCompound: string;
}

export interface Group {
  valid: Valid<null | Dayjs>;
  groupId?: number;
  name: string;
}

export const AIMatchKey = 'notification-settings';

export enum AIMatchSettingsType {
  TIMES = 'times',
  BEFORE_START_LIMIT = 'minutes-before-start-limit',
  NON_FLEX_ENABLED = 'nonFlexEnabled',
  FLEX_ENABLED = 'flexEnabled',
  ROUND_MINUTES_BEFORE_START_LIMIT = 'minutesBeforeStartLimit'
}
